import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { db } from '@/config/firebase';
import { doc, onSnapshot, collection, query, where, orderBy, addDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || (import.meta && import.meta.env && import.meta.env.REACT_APP_BACKEND_URL);

export default function TournamentDetail() {
  const { id: tournamentId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [tournament, setTournament] = useState(null);
  const [players, setPlayers] = useState([]);
  const [currentRound, setCurrentRound] = useState(null);
  const [matches, setMatches] = useState([]);
  const [allRounds, setAllRounds] = useState([]);
  const [allMatches, setAllMatches] = useState([]);
  const [tab, setTab] = useState('matches');
  const [loading, setLoading] = useState(true);
  
  // Timer state
  const [timerState, setTimerState] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [isBreak, setIsBreak] = useState(false);
  const audioRef = useRef(null);
  const timerIntervalRef = useRef(null);

  // Derived state - must be before useEffects that use it
  const isOwner = tournament?.owner_id === user?.uid;
  const canEnterScores = timerState?.status === 'stopped' || secondsLeft === 0;

  // Load tournament
  useEffect(() => {
    if (!tournamentId) return;
    
    let retryCount = 0;
    const maxRetries = 5;
    
    const attemptLoad = () => {
      const unsubTournament = onSnapshot(
        doc(db, 'tournaments', tournamentId), 
        (doc) => {
          if (doc.exists()) {
            setTournament({ id: doc.id, ...doc.data() });
            setLoading(false);
          } else if (retryCount < maxRetries) {
            // Retry after delay
            retryCount++;
            console.log(`Tournament not found, retrying (${retryCount}/${maxRetries})...`);
            setTimeout(attemptLoad, 1000);
          } else {
            console.error('Tournament not found after retries');
            setLoading(false);
          }
        },
        (error) => {
          console.error('Tournament listener error:', error);
          setLoading(false);
        }
      );

      return unsubTournament;
    };
    
    const unsub = attemptLoad();
    return () => {
      if (unsub) unsub();
    };
  }, [tournamentId]);

  // Load players
  useEffect(() => {
    if (!tournamentId) return;
    
    const q = query(collection(db, 'tournament_players'), where('tournament_id', '==', tournamentId));
    const unsubPlayers = onSnapshot(
      q, 
      (snapshot) => {
        const playersData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setPlayers(playersData);
      },
      (error) => {
        console.error('Players listener error:', error);
      }
    );

    return () => unsubPlayers();
  }, [tournamentId]);

  // Load current round and matches - only for running/finished tournaments
  useEffect(() => {
    // Skip listener for draft tournaments or when no rounds exist
    if (!tournament || tournament.status === 'draft') {
      setCurrentRound(null);
      setMatches([]);
      setAllRounds([]);
      setAllMatches([]);
      return;
    }
    
    if (!tournament.current_round_index || tournament.current_round_index === 0) {
      setCurrentRound(null);
      setMatches([]);
      setAllRounds([]);
      setAllMatches([]);
      return;
    }

    let unsubMatches = null;

    // Load ALL rounds for this tournament (no orderBy to avoid index requirement)
    const roundsQuery = query(
      collection(db, 'tournament_rounds'),
      where('tournament_id', '==', tournamentId)
    );

    const unsubRounds = onSnapshot(
      roundsQuery, 
      (snapshot) => {
        // Get all rounds and sort by index
        const rounds = snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .sort((a, b) => (a.index || 0) - (b.index || 0));
        
        setAllRounds(rounds);
        
        // Find the current round by index
        const currentRoundData = rounds.find(r => r.index === tournament.current_round_index);
        
        if (currentRoundData) {
          setCurrentRound(currentRoundData);
        } else {
          setCurrentRound(null);
        }

        // Load ALL matches for this tournament
        const matchesQuery = query(
          collection(db, 'tournament_matches'),
          where('tournament_id', '==', tournamentId)
        );

        unsubMatches = onSnapshot(
          matchesQuery, 
          (matchSnapshot) => {
            const allMatchesData = matchSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setAllMatches(allMatchesData);
            
            // Filter by current round and sort by court_number
            if (currentRoundData) {
              const roundMatches = allMatchesData
                .filter(m => m.round_id === currentRoundData.id)
                .sort((a, b) => (a.court_number || 0) - (b.court_number || 0));
              setMatches(roundMatches);
            } else {
              setMatches([]);
            }
          },
          (error) => {
            console.error('Matches listener error:', error);
            setMatches([]);
            setAllMatches([]);
          }
        );
      },
      (error) => {
        console.error('Rounds listener error:', error);
        setCurrentRound(null);
        setMatches([]);
        setAllRounds([]);
      }
    );

    return () => {
      unsubRounds();
      if (unsubMatches) unsubMatches();
    };
  }, [tournament, tournamentId]);

  // Timer listener - only for running tournaments
  useEffect(() => {
    // Only setup timer listener for running tournaments
    if (!tournamentId || !tournament || tournament.status !== 'running') {
      setTimerState(null);
      setSecondsLeft(0);
      setIsBreak(false);
      return;
    }

    const timerDoc = doc(db, 'tournament_timers', tournamentId);
    const unsubTimer = onSnapshot(
      timerDoc, 
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          const prevTimerState = timerState;
          setTimerState(data);
          
          // Always sync isBreak from Firestore - this is the source of truth
          setIsBreak(data.is_break === true);
          
          if (data.status === 'running') {
            const endTime = data.end_time?.toDate?.() || new Date(data.end_time);
            const now = new Date();
            const diffSeconds = Math.floor((endTime - now) / 1000);
            setSecondsLeft(Math.max(0, diffSeconds));
            
            // Log for debugging
            console.log(`Timer update: is_break=${data.is_break}, seconds=${diffSeconds}, round=${data.round_index}`);
          } else {
            setSecondsLeft(0);
          }
        } else {
          setTimerState(null);
          setSecondsLeft(0);
          setIsBreak(false);
        }
      },
      (error) => {
        console.error('Timer listener error:', error);
        setTimerState(null);
        setSecondsLeft(0);
      }
    );

    return () => unsubTimer();
  }, [tournamentId, tournament]);

  // Sound enabled state (persisted per device)
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem('tournament_sound_enabled');
    return saved === null ? false : saved === 'true'; // Default OFF
  });

  // Track which sounds have been played to prevent duplicates
  const playedSoundsRef = useRef(new Set());

  // Toggle sound
  const toggleSound = () => {
    const newValue = !soundEnabled;
    setSoundEnabled(newValue);
    localStorage.setItem('tournament_sound_enabled', newValue.toString());
    if (!newValue && 'speechSynthesis' in window) {
      speechSynthesis.cancel(); // Stop any ongoing speech
    }
  };

  // Custom sound URLs
  const END_SOUND_URL = 'https://customer-assets.emergentagent.com/job_mixer-tourney/artifacts/wdqa1vjf_ElevenLabs_2026-01-10T11_06_39_Horvath%20aus%20Wien_pvc_sp75_s38_sb30_se23_b_m2.mp3';
  const TRANSITION_END_SOUND_URL = 'https://customer-assets.emergentagent.com/job_mixer-tourney/artifacts/uyu27pw6_ElevenLabs_2026-01-10T11_12_03_Horvath%20aus%20Wien_pvc_sp75_s38_sb30_se23_b_m2.mp3';

  // Transition timer duration (2 minutes)
  const TRANSITION_DURATION = 120;

  // Play sound function - only if enabled and not already played for this second
  const playSound = (type, currentSecond) => {
    if (!soundEnabled) return;
    
    const soundKey = `${type}_${currentSecond}`;
    if (playedSoundsRef.current.has(soundKey)) return;
    playedSoundsRef.current.add(soundKey);
    
    // Clean up old keys after 5 seconds
    setTimeout(() => {
      playedSoundsRef.current.delete(soundKey);
    }, 5000);
    
    try {
      const beep = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBi0A');
      
      switch(type) {
        case 'start':
          beep.play().catch(() => {});
          if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
          break;
        case 'end':
          // Play custom end sound (round end)
          const endSound = new Audio(END_SOUND_URL);
          endSound.volume = 1.0;
          endSound.play().catch(() => {});
          if (navigator.vibrate) navigator.vibrate([300, 100, 300, 100, 300]);
          break;
        case 'transition_end':
          // Play transition end sound (break over, next round starts)
          const transitionSound = new Audio(TRANSITION_END_SOUND_URL);
          transitionSound.volume = 1.0;
          transitionSound.play().catch(() => {});
          if (navigator.vibrate) navigator.vibrate([500, 200, 500]);
          break;
        case 'thirty':
          beep.play().catch(() => {});
          if (navigator.vibrate) navigator.vibrate(100);
          if ('speechSynthesis' in window) {
            speechSynthesis.cancel();
            const utterance = new SpeechSynthesisUtterance('30 Sekunden');
            utterance.lang = 'de-DE';
            utterance.rate = 1.2;
            speechSynthesis.speak(utterance);
          }
          break;
        case 'countdown':
          beep.play().catch(() => {});
          if (navigator.vibrate) navigator.vibrate(50);
          if ('speechSynthesis' in window && currentSecond <= 10 && currentSecond > 0) {
            speechSynthesis.cancel();
            const utterance = new SpeechSynthesisUtterance(currentSecond.toString());
            utterance.lang = 'de-DE';
            utterance.rate = 1.2;
            speechSynthesis.speak(utterance);
          }
          break;
        default:
          break;
      }
    } catch (e) {
      console.error('Sound error:', e);
    }
  };

  // Timer countdown - separate from sound logic
  useEffect(() => {
    // Cancel speech when timer stops
    if (!timerState || timerState.status !== 'running') {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      if ('speechSynthesis' in window) {
        speechSynthesis.cancel();
      }
      playedSoundsRef.current.clear();
      return;
    }

    // Don't create duplicate intervals
    if (timerIntervalRef.current) return;

    timerIntervalRef.current = setInterval(() => {
      setSecondsLeft(prev => {
        const newValue = prev - 1;
        
        if (newValue < 0) {
          return 0;
        }
        
        return newValue;
      });
    }, 1000);

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [timerState?.status]);

  // Separate effect for sound cues - triggered by secondsLeft changes
  useEffect(() => {
    if (!soundEnabled || !timerState || timerState.status !== 'running') return;
    
    if (secondsLeft === 30) {
      playSound('thirty', 30);
    } else if (secondsLeft <= 10 && secondsLeft > 0) {
      playSound('countdown', secondsLeft);
    } else if (secondsLeft === 0 && !isBreak) {
      // Only play end sound for round end, NOT for transition end
      // Transition end sound is played in the auto-start useEffect
      playSound('end', 0);
    }
  }, [secondsLeft, soundEnabled, timerState?.status, isBreak]);

  // Check if all matches are finished and auto-start transition
  useEffect(() => {
    if (!isOwner || !tournament || tournament.status !== 'running') return;
    if (matches.length === 0) return;
    
    const allFinished = matches.every(m => m.status === 'finished');
    const timerStopped = !timerState || timerState.status !== 'running' || secondsLeft === 0;
    
    // Don't auto-transition if already in break mode or not owner
    if (allFinished && timerStopped && !isBreak && isOwner) {
      // Check if tournament should end
      const totalDuration = tournament.total_duration_minutes * 60;
      const roundDuration = tournament.round_duration_seconds;
      const breakDuration = tournament.break_duration_seconds || TRANSITION_DURATION;
      const cycleTime = roundDuration + breakDuration;
      const maxRounds = Math.floor(totalDuration / cycleTime);
      
      if (tournament.current_round_index >= maxRounds) {
        // Tournament should end - ask admin
        if (window.confirm(`🏆 ${maxRounds} Runden gespielt!\n\nTurnier beenden?`)) {
          endTournament();
          return;
        }
      }
      
      // Auto-start transition timer after short delay
      const timeoutId = setTimeout(() => {
        startTransitionWithNextRound();
      }, 2000);
      
      return () => clearTimeout(timeoutId);
    }
  }, [matches, timerState, secondsLeft, isBreak, isOwner, tournament]);

  // Track if we already triggered auto-start to prevent double triggers
  const autoStartTriggeredRef = useRef(false);
  const lastRoundIndexRef = useRef(0);

  // Auto-start round timer when transition ends
  useEffect(() => {
    // Reset trigger when round index changes (new round started)
    if (tournament?.current_round_index !== lastRoundIndexRef.current) {
      console.log(`Round changed: ${lastRoundIndexRef.current} -> ${tournament?.current_round_index}, resetting trigger`);
      lastRoundIndexRef.current = tournament?.current_round_index || 0;
      autoStartTriggeredRef.current = false;
    }
    
    // Debug log
    console.log(`Timer check: isBreak=${isBreak}, seconds=${secondsLeft}, round=${tournament?.current_round_index}, triggered=${autoStartTriggeredRef.current}`);
    
    // Not in break or timer still running - do nothing
    if (!isBreak || secondsLeft > 0) {
      return;
    }
    
    // Only owner can trigger auto-start
    if (!isOwner) return;
    
    // Prevent double triggers
    if (autoStartTriggeredRef.current) {
      return;
    }
    
    // Transition ended (isBreak=true, secondsLeft<=0) - start the round timer
    console.log('🎯 Transition ended! Starting round timer for round', tournament?.current_round_index);
    autoStartTriggeredRef.current = true;
    
    // Play transition end sound FIRST
    if (soundEnabled) {
      try {
        const transitionSound = new Audio('https://customer-assets.emergentagent.com/job_mixer-tourney/artifacts/uyu27pw6_ElevenLabs_2026-01-10T11_12_03_Horvath%20aus%20Wien_pvc_sp75_s38_sb30_se23_b_m2.mp3');
        transitionSound.volume = 1.0;
        transitionSound.play().catch(e => console.log('Audio play failed:', e));
      } catch (e) {
        console.log('Audio error:', e);
      }
    }
    
    const timeoutId = setTimeout(async () => {
      console.log('⏰ Executing startRoundAfterTransition...');
      await startRoundAfterTransition();
    }, 2000);
    
    return () => clearTimeout(timeoutId);
  }, [isBreak, secondsLeft, isOwner, soundEnabled, tournament?.current_round_index]);

  // End tournament
  const endTournament = async () => {
    try {
      const tournamentRef = doc(db, 'tournaments', tournamentId);
      await setDoc(tournamentRef, {
        status: 'finished',
        finished_at: serverTimestamp()
      }, { merge: true });
      
      // Stop any running timer
      const timerRef = doc(db, 'tournament_timers', tournamentId);
      await setDoc(timerRef, {
        status: 'stopped'
      }, { merge: true });
      
      alert('🏆 Turnier beendet!');
    } catch (error) {
      console.error('Failed to end tournament:', error);
    }
  };

  // Start transition AND generate next round immediately so players can see it
  const startTransitionWithNextRound = async () => {
    try {
      // First generate the next round so it's visible during transition
      const response = await fetch(`${BACKEND_URL}/api/tournaments/${tournamentId}/generate_round`, {
        method: 'POST'
      });
      const data = await response.json();
      
      if (!data.ok) {
        console.error('Failed to generate next round:', data.message);
        return;
      }
      
      // Then start the transition timer
      const breakDuration = tournament.break_duration_seconds || TRANSITION_DURATION;
      const now = new Date();
      const endTime = new Date(now.getTime() + breakDuration * 1000);

      const timerRef = doc(db, 'tournament_timers', tournamentId);
      await setDoc(timerRef, {
        tournament_id: tournamentId,
        status: 'running',
        start_time: serverTimestamp(),
        end_time: endTime,
        is_break: true,
        round_index: data.round_index
      });

      setIsBreak(true);
      setSecondsLeft(breakDuration);
    } catch (error) {
      console.error('Failed to start transition:', error);
    }
  };

  // Start round timer AFTER transition (different from manual startRound)
  const startRoundAfterTransition = async () => {
    console.log('🚀 startRoundAfterTransition called!');
    try {
      const roundDuration = tournament.round_duration_seconds;
      const now = new Date();
      const endTime = new Date(now.getTime() + roundDuration * 1000);

      console.log(`📝 Writing to Firestore: is_break=false, duration=${roundDuration}s`);
      
      const timerRef = doc(db, 'tournament_timers', tournamentId);
      await setDoc(timerRef, {
        tournament_id: tournamentId,
        status: 'running',
        start_time: serverTimestamp(),
        end_time: endTime,
        is_break: false,
        round_index: tournament.current_round_index
      });

      console.log('✅ Firestore updated successfully!');
      
      // Play start sound
      playSound('start', 0);
    } catch (error) {
      console.error('❌ Failed to start round after transition:', error);
    }
  };

  const startTransition = async () => {
    try {
      const breakDuration = tournament.break_duration_seconds || TRANSITION_DURATION;
      const now = new Date();
      const endTime = new Date(now.getTime() + breakDuration * 1000);

      const timerRef = doc(db, 'tournament_timers', tournamentId);
      await setDoc(timerRef, {
        tournament_id: tournamentId,
        status: 'running',
        start_time: serverTimestamp(),
        end_time: endTime,
        is_break: true,
        round_index: tournament.current_round_index
      });

      setIsBreak(true);
      setSecondsLeft(breakDuration);
    } catch (error) {
      console.error('Failed to start transition:', error);
    }
  };

  const startRound = async () => {
    try {
      const roundDuration = tournament.round_duration_seconds;
      const now = new Date();
      const endTime = new Date(now.getTime() + roundDuration * 1000);

      // Use tournament_id as document ID for timer (single timer per tournament)
      const timerRef = doc(db, 'tournament_timers', tournamentId);
      await setDoc(timerRef, {
        tournament_id: tournamentId,
        status: 'running',
        start_time: serverTimestamp(),
        end_time: endTime,
        is_break: false,
        round_index: tournament.current_round_index
      });

      // IMPORTANT: Set both states
      setIsBreak(false);
      setSecondsLeft(roundDuration);
      playSound('start', 0);
      
      // Send notification to all players
      await addDoc(collection(db, 'tournament_notifications'), {
        tournament_id: tournamentId,
        message: `🎾 Runde ${tournament.current_round_index} gestartet!`,
        created_at: serverTimestamp()
      });
    } catch (error) {
      console.error('Failed to start round:', error);
      alert('Fehler beim Starten der Runde');
    }
  };

  const generateNextRound = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/tournaments/${tournamentId}/generate_round`, {
        method: 'POST'
      });

      const data = await response.json();

      if (data.ok) {
        alert(`✅ ${data.message}`);
      } else {
        alert(`❌ Fehler: ${data.message || 'Runde konnte nicht generiert werden'}`);
      }
    } catch (error) {
      console.error('Failed to generate round:', error);
      alert('Fehler beim Generieren der Runde');
    }
  };

  const updateScore = async (matchId, scoreTeam1, scoreTeam2) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/tournaments/${tournamentId}/matches/${matchId}/score`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ score_team1: scoreTeam1, score_team2: scoreTeam2 })
      });

      const data = await response.json();

      if (data.ok) {
        alert('✅ Ergebnis gespeichert!');
      } else {
        alert(`❌ Fehler: ${data.message}`);
      }
    } catch (error) {
      console.error('Failed to update score:', error);
      alert('Fehler beim Speichern des Ergebnisses');
    }
  };

  // Admin functions
  const stopTimer = async () => {
    try {
      const timerRef = doc(db, 'tournament_timers', tournamentId);
      await setDoc(timerRef, {
        tournament_id: tournamentId,
        status: 'stopped',
        is_break: false
      }, { merge: true });
      setSecondsLeft(0);
    } catch (error) {
      console.error('Failed to stop timer:', error);
      alert('Fehler beim Stoppen des Timers');
    }
  };

  const setCustomTimer = async () => {
    const minutesInput = document.getElementById('customMinutes');
    const secondsInput = document.getElementById('customSeconds');
    const minutes = parseInt(minutesInput?.value || '0') || 0;
    const seconds = parseInt(secondsInput?.value || '0') || 0;
    const totalSeconds = minutes * 60 + seconds;
    
    if (totalSeconds <= 0) {
      alert('Bitte gültige Zeit eingeben');
      return;
    }
    
    try {
      const now = new Date();
      const endTime = new Date(now.getTime() + totalSeconds * 1000);
      
      const timerRef = doc(db, 'tournament_timers', tournamentId);
      await setDoc(timerRef, {
        tournament_id: tournamentId,
        status: 'running',
        start_time: serverTimestamp(),
        end_time: endTime,
        is_break: false,
        round_index: tournament.current_round_index
      });
      
      setSecondsLeft(totalSeconds);
      
      // Clear inputs
      if (minutesInput) minutesInput.value = '';
      if (secondsInput) secondsInput.value = '';
    } catch (error) {
      console.error('Failed to set custom timer:', error);
      alert('Fehler beim Setzen des Timers');
    }
  };

  const addTimeToTimer = async (secondsToAdd) => {
    try {
      const newSeconds = Math.max(0, secondsLeft + secondsToAdd);
      const now = new Date();
      const endTime = new Date(now.getTime() + newSeconds * 1000);
      
      const timerRef = doc(db, 'tournament_timers', tournamentId);
      await setDoc(timerRef, {
        tournament_id: tournamentId,
        status: newSeconds > 0 ? 'running' : 'stopped',
        start_time: serverTimestamp(),
        end_time: endTime,
        is_break: isBreak,
        round_index: tournament.current_round_index
      });
      
      setSecondsLeft(newSeconds);
    } catch (error) {
      console.error('Failed to add time:', error);
      alert('Fehler beim Anpassen der Zeit');
    }
  };

  const resetTimer = async () => {
    if (!window.confirm('Timer wirklich zurücksetzen?')) return;
    try {
      const timerRef = doc(db, 'tournament_timers', tournamentId);
      await setDoc(timerRef, {
        tournament_id: tournamentId,
        status: 'stopped',
        is_break: false,
        round_index: tournament.current_round_index
      });
      setSecondsLeft(0);
      setTimerState(null);
      alert('✅ Timer zurückgesetzt');
    } catch (error) {
      console.error('Failed to reset timer:', error);
      alert('Fehler beim Zurücksetzen des Timers');
    }
  };

  const goToPreviousRound = async () => {
    if (!tournament.current_round_index || tournament.current_round_index <= 1) {
      alert('Keine vorherige Runde vorhanden');
      return;
    }
    if (!window.confirm(`Zur Runde ${tournament.current_round_index - 1} zurückkehren?`)) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/tournaments/${tournamentId}/set_round`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ round_index: tournament.current_round_index - 1 })
      });
      const data = await response.json();
      if (data.ok) {
        alert(`✅ Zurück zu Runde ${tournament.current_round_index - 1}`);
      } else {
        alert(`❌ Fehler: ${data.message}`);
      }
    } catch (error) {
      console.error('Failed to go to previous round:', error);
      alert('Fehler beim Wechseln der Runde');
    }
  };

  const resetTournament = async () => {
    if (!window.confirm('⚠️ ACHTUNG: Alle Runden, Matches und Ergebnisse werden gelöscht! Fortfahren?')) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/tournaments/${tournamentId}/reset`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.ok) {
        alert('✅ Turnier zurückgesetzt');
        // Reset timer state
        setTimerState(null);
        setSecondsLeft(0);
      } else {
        alert(`❌ Fehler: ${data.message}`);
      }
    } catch (error) {
      console.error('Failed to reset tournament:', error);
      alert('Fehler beim Zurücksetzen des Turniers');
    }
  };

  const getPlayerName = (playerId) => {
    const player = players.find(p => p.id === playerId);
    return player?.name || 'Unbekannt';
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return <div className="min-h-screen bg-[#0d0409] text-rose-100 p-4 flex items-center justify-center">Laden...</div>;
  }

  if (!tournament) {
    return <div className="min-h-screen bg-[#0d0409] text-rose-100 p-4 flex items-center justify-center">Turnier nicht gefunden</div>;
  }

  return (
    <div className="min-h-screen bg-[#0d0409] text-rose-100 p-4 pb-20">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Button onClick={() => navigate('/tournaments')} variant="ghost" className="text-rose-200">
              ← Zurück
            </Button>
            <h1 className="text-2xl font-bold text-rose-200">{tournament.name}</h1>
          </div>
          <span className={`px-3 py-1 rounded text-sm ${
            tournament.status === 'running' ? 'bg-green-600' : 
            tournament.status === 'finished' ? 'bg-blue-600' : 'bg-gray-500'
          }`}>
            {tournament.status === 'running' ? 'Läuft' : tournament.status === 'finished' ? 'Beendet' : 'Entwurf'}
          </span>
        </div>

        {/* Draft Tournament Setup Panel */}
        {tournament.status === 'draft' && (
          <Card className="bg-[#1a0d11] border-orange-500/50 mb-4">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-4xl mb-4">📋</div>
                <h2 className="text-xl font-bold text-orange-400 mb-2">Turnier-Entwurf</h2>
                <p className="text-rose-200/70 mb-4">
                  Dieses Turnier ist noch nicht gestartet.
                </p>
                
                {/* Tournament Info */}
                <div className="bg-white/5 rounded-lg p-4 mb-4 text-left">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="text-rose-200/60">Spieler:</div>
                    <div className="text-rose-200 font-medium">{players.length}</div>
                    <div className="text-rose-200/60">Plätze:</div>
                    <div className="text-rose-200 font-medium">{tournament.courts}</div>
                    <div className="text-rose-200/60">Rundendauer:</div>
                    <div className="text-rose-200 font-medium">{tournament.round_duration_seconds / 60} Min</div>
                    <div className="text-rose-200/60">Pausendauer:</div>
                    <div className="text-rose-200 font-medium">{tournament.break_duration_seconds / 60} Min</div>
                  </div>
                </div>

                {/* Player List */}
                {players.length > 0 && (
                  <div className="bg-white/5 rounded-lg p-3 mb-4 text-left">
                    <h3 className="text-xs font-semibold text-rose-200/60 mb-2">Teilnehmer ({players.length}):</h3>
                    <div className="flex flex-wrap gap-1">
                      {players.map(player => (
                        <span key={player.id} className="px-2 py-0.5 bg-[#b40023]/30 rounded text-xs">
                          {player.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {isOwner && (
                  <Button 
                    onClick={generateNextRound} 
                    className="w-full bg-green-700 hover:bg-green-800"
                    data-testid="generate-matches-btn"
                  >
                    🎲 Matches generieren & Turnier starten
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Timer */}
        {tournament.status === 'running' && (
          <Card className={`mb-3 ${isBreak ? 'bg-[#1a0d11] border-blue-500/50' : 'bg-[#1a0d11] border-yellow-500/50'}`}>
            <CardContent className="pt-4 pb-3">
              <div className="text-center">
                <div className={`text-5xl font-bold mb-1 ${isBreak ? 'text-blue-400' : 'text-yellow-400'}`}>
                  {formatTime(secondsLeft)}
                </div>
                <div className="text-sm text-rose-200/70">
                  {isBreak ? `⏸️ PAUSE - Runde ${tournament.current_round_index} startet gleich!` : `🎾 Runde ${tournament.current_round_index}`}
                </div>
                
                {/* Progress bar + Sound toggle in one row */}
                <div className="flex items-center gap-2 mt-2">
                  <div className="flex-1 bg-white/10 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-1000 ${isBreak ? 'bg-blue-400' : 'bg-yellow-400'}`}
                      style={{ width: `${(secondsLeft / (isBreak ? (tournament.break_duration_seconds || 120) : tournament.round_duration_seconds)) * 100}%` }}
                    />
                  </div>
                  <button
                    onClick={toggleSound}
                    className={`px-3 py-1 rounded-full text-xs ${soundEnabled ? 'bg-green-600' : 'bg-white/10'}`}
                  >
                    {soundEnabled ? '🔊' : '🔇'}
                  </button>
                </div>
                
                {/* Break message - compact */}
                {isBreak && (
                  <div className="mt-2 text-xs text-blue-300">
                    🏃 Wechselt zu euren Courts!
                  </div>
                )}
                
                {/* Admin Controls - Compact */}
                {isOwner && (
                  <details className="mt-3 pt-3 border-t border-white/10 text-left">
                    <summary className="cursor-pointer text-rose-200/60 text-xs">⚙️ Admin</summary>
                    <div className="mt-2 space-y-2">
                      {/* Timer row */}
                      <div className="flex flex-wrap gap-1 items-center">
                        {timerState?.status === 'running' ? (
                          <button onClick={stopTimer} className="px-2 py-1 bg-orange-600 rounded text-xs">⏹️ Stop</button>
                        ) : (
                          <button onClick={startRound} className="px-2 py-1 bg-green-700 rounded text-xs">▶️ Start</button>
                        )}
                        <input type="number" id="customMinutes" placeholder="Min" className="w-12 bg-[#0d0409] border border-white/20 rounded px-1 py-1 text-xs text-center" />
                        <span className="text-xs">:</span>
                        <input type="number" id="customSeconds" placeholder="Sek" className="w-12 bg-[#0d0409] border border-white/20 rounded px-1 py-1 text-xs text-center" />
                        <button onClick={setCustomTimer} className="px-2 py-1 border border-yellow-500/30 rounded text-xs text-yellow-400">⏱️</button>
                      </div>
                      {/* Quick buttons */}
                      <div className="flex flex-wrap gap-1">
                        <button onClick={() => addTimeToTimer(60)} className="px-2 py-1 border border-green-500/30 rounded text-xs text-green-400">+1m</button>
                        <button onClick={() => addTimeToTimer(-60)} className="px-2 py-1 border border-red-500/30 rounded text-xs text-red-400">-1m</button>
                        <button onClick={resetTimer} className="px-2 py-1 border border-orange-500/30 rounded text-xs text-orange-400">🔄</button>
                        <button onClick={goToPreviousRound} className="px-2 py-1 border border-blue-500/30 rounded text-xs text-blue-400">⏮️</button>
                        <button onClick={generateNextRound} className="px-2 py-1 bg-blue-700 rounded text-xs">⏭️</button>
                        <button onClick={resetTournament} className="px-2 py-1 border border-red-500/30 rounded text-xs text-red-400">🗑️</button>
                      </div>
                    </div>
                  </details>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-3 overflow-x-auto">
          <button 
            onClick={() => setTab('matches')} 
            className={`px-3 py-1.5 rounded text-sm whitespace-nowrap ${tab === 'matches' ? 'bg-[#b40023]' : 'bg-white/10'}`}
          >
            Aktuelle Runde
          </button>
          <button 
            onClick={() => setTab('schedule')} 
            className={`px-3 py-1.5 rounded text-sm whitespace-nowrap ${tab === 'schedule' ? 'bg-[#b40023]' : 'bg-white/10'}`}
          >
            Alle Runden
          </button>
          <button 
            onClick={() => setTab('standings')} 
            className={`px-3 py-1.5 rounded text-sm whitespace-nowrap ${tab === 'standings' ? 'bg-[#b40023]' : 'bg-white/10'}`}
          >
            Tabelle
          </button>
        </div>

        {/* Current Round Tab */}
        {tab === 'matches' && (
          <div className="space-y-2">
            {matches.length === 0 ? (
              <Card className="bg-[#1a0d11] border-white/10">
                <CardContent className="pt-4 text-center">
                  <p className="text-rose-200/70 mb-3">Noch keine Matches</p>
                  {isOwner && tournament.status !== 'draft' && (
                    <Button onClick={generateNextRound} className="bg-blue-700 hover:bg-blue-800">
                      Erste Runde generieren
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Break/Transition notice - compact */}
                {isBreak && (
                  <div className="bg-blue-900/30 border border-blue-500/50 rounded-lg p-2 mb-2 text-center">
                    <span className="text-blue-300 text-sm font-semibold">
                      🆕 Runde {tournament.current_round_index} - Macht euch bereit!
                    </span>
                  </div>
                )}
                
                <div className="text-xs text-rose-200/60 mb-1">
                  Runde {currentRound?.index || tournament.current_round_index} von {allRounds.length}
                </div>
                {matches.map(match => (
                  <MatchCard 
                    key={match.id}
                    match={match}
                    getPlayerName={getPlayerName}
                    canEnterScores={canEnterScores}
                    updateScore={updateScore}
                  />
                ))}
              </>
            )}
          </div>
        )}

        {/* All Rounds Schedule Tab */}
        {tab === 'schedule' && (
          <div className="space-y-4">
            {allRounds.length === 0 ? (
              <Card className="bg-[#1a0d11] border-white/10">
                <CardContent className="pt-6 text-center">
                  <p className="text-rose-200/70">Noch keine Runden generiert</p>
                </CardContent>
              </Card>
            ) : (
              allRounds.map(round => {
                const roundMatches = allMatches
                  .filter(m => m.round_id === round.id)
                  .sort((a, b) => (a.court_number || 0) - (b.court_number || 0));
                const isCurrent = round.index === tournament.current_round_index;
                
                return (
                  <Card 
                    key={round.id} 
                    className={`bg-[#1a0d11] ${isCurrent ? 'border-yellow-500/50 border-2' : 'border-white/10'}`}
                  >
                    <CardContent className="pt-3 pb-2">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-semibold text-sm">
                          Runde {round.index}
                          {isCurrent && <span className="ml-1 text-yellow-400 text-xs">(Aktuell)</span>}
                        </span>
                        <span className={`px-1.5 py-0.5 rounded text-xs ${
                          roundMatches.every(m => m.status === 'finished') ? 'bg-green-600' : 
                          roundMatches.some(m => m.status === 'finished') ? 'bg-yellow-600' : 'bg-gray-600'
                        }`}>
                          {roundMatches.filter(m => m.status === 'finished').length}/{roundMatches.length}
                        </span>
                      </div>
                      
                      <div className="space-y-1">
                        {roundMatches.map(match => {
                          const t1 = `${getPlayerName(match.team1_player_ids?.[0])} & ${getPlayerName(match.team1_player_ids?.[1])}`;
                          const t2 = `${getPlayerName(match.team2_player_ids?.[0])} & ${getPlayerName(match.team2_player_ids?.[1])}`;
                          return (
                            <div 
                              key={match.id} 
                              className={`p-2 rounded text-xs ${match.status === 'finished' ? 'bg-green-900/20' : 'bg-white/5'}`}
                            >
                              <div className="flex justify-between items-center">
                                <div className="flex-1">
                                  <span className="text-rose-200/50">C{match.court_number}:</span>{' '}
                                  <span className={match.team1_is_home ? 'text-yellow-400' : ''}>{t1}</span>
                                  <span className="text-rose-200/40"> vs </span>
                                  <span className={!match.team1_is_home ? 'text-yellow-400' : ''}>{t2}</span>
                                </div>
                                {match.status === 'finished' && (
                                  <span className="font-bold ml-2">{match.score_team1}:{match.score_team2}</span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        )}

        {/* Standings Tab */}
        {tab === 'standings' && (
          <Card className="bg-[#1a0d11] border-white/10">
            <CardContent className="pt-3">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-white/10 text-rose-200/60">
                      <th className="text-left p-1.5">Spieler</th>
                      <th className="text-center p-1.5">Pkt</th>
                      <th className="text-center p-1.5">Sp</th>
                      <th className="text-center p-1.5">S/U/N</th>
                      <th className="text-center p-1.5">+/-</th>
                    </tr>
                  </thead>
                  <tbody>
                    {players
                      .sort((a, b) => {
                        const ptsA = a.stats?.tournament_points || 0;
                        const ptsB = b.stats?.tournament_points || 0;
                        if (ptsB !== ptsA) return ptsB - ptsA;
                        return (b.stats?.diff || 0) - (a.stats?.diff || 0);
                      })
                      .map((player, index) => (
                        <tr key={player.id} className={`border-b border-white/10 ${index < 3 ? 'bg-yellow-500/10' : ''}`}>
                          <td className="p-1.5 font-medium">{player.name}</td>
                          <td className="text-center p-1.5 font-bold text-yellow-400">{player.stats?.tournament_points || 0}</td>
                          <td className="text-center p-1.5">{player.stats?.games_played || 0}</td>
                          <td className="text-center p-1.5">
                            <span className="text-green-400">{player.stats?.wins || 0}</span>/
                            <span className="text-gray-400">{player.stats?.draws || 0}</span>/
                            <span className="text-red-400">{player.stats?.losses || 0}</span>
                          </td>
                          <td className="text-center p-1.5 font-bold">{player.stats?.diff > 0 ? '+' : ''}{player.stats?.diff || 0}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

// Compact Match Card Component
function MatchCard({ match, getPlayerName, canEnterScores, updateScore }) {
  const [scoreTeam1, setScoreTeam1] = useState(match.score_team1 ?? '');
  const [scoreTeam2, setScoreTeam2] = useState(match.score_team2 ?? '');
  const [showScoreInput, setShowScoreInput] = useState(false);

  const handleSave = () => {
    const s1 = parseInt(scoreTeam1);
    const s2 = parseInt(scoreTeam2);
    if (isNaN(s1) || isNaN(s2)) {
      alert('Bitte gültige Zahlen eingeben');
      return;
    }
    updateScore(match.id, s1, s2);
    setShowScoreInput(false);
  };

  const team1Names = `${getPlayerName(match.team1_player_ids?.[0])} & ${getPlayerName(match.team1_player_ids?.[1])}`;
  const team2Names = `${getPlayerName(match.team2_player_ids?.[0])} & ${getPlayerName(match.team2_player_ids?.[1])}`;
  const isDraw = match.status === 'finished' && match.score_team1 === match.score_team2;

  return (
    <div className={`bg-[#1a0d11] border rounded-lg p-3 ${match.status === 'finished' ? 'border-green-500/30' : 'border-white/10'}`}>
      {/* Header row */}
      <div className="flex justify-between items-center mb-2 text-xs text-rose-200/60">
        <span>Court {match.court_number}</span>
        {match.referee_id && (
          <span className="bg-purple-600/20 text-purple-300 px-2 py-0.5 rounded">
            🧑‍⚖️ {getPlayerName(match.referee_id)}
          </span>
        )}
      </div>

      {/* Teams & Score */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1 text-sm">
          <div className={match.team1_is_home ? 'text-yellow-400' : ''}>
            {team1Names} {match.team1_is_home && '🏠'}
          </div>
        </div>
        
        {match.status === 'finished' ? (
          <div className={`text-xl font-bold px-3 ${isDraw ? 'text-gray-400' : ''}`}>
            {match.score_team1} : {match.score_team2}
          </div>
        ) : (
          <div className="text-xs text-rose-200/40">vs</div>
        )}
        
        <div className="flex-1 text-sm text-right">
          <div className={!match.team1_is_home && match.team1_is_home !== undefined ? 'text-yellow-400' : ''}>
            {!match.team1_is_home && match.team1_is_home !== undefined && '🏠 '}{team2Names}
          </div>
        </div>
      </div>

      {/* Score Input */}
      {match.status !== 'finished' && canEnterScores && (
        <div className="mt-2 pt-2 border-t border-white/10">
          {!showScoreInput ? (
            <button 
              onClick={() => setShowScoreInput(true)}
              className="w-full py-2 bg-green-700 hover:bg-green-800 rounded text-sm"
            >
              📝 Ergebnis
            </button>
          ) : (
            <div className="flex gap-2 items-center">
              <input 
                type="number"
                inputMode="numeric"
                value={scoreTeam1}
                onChange={e => setScoreTeam1(e.target.value)}
                className="w-16 bg-[#0d0409] border border-white/20 rounded px-2 py-1 text-center"
                placeholder="0"
              />
              <span>:</span>
              <input 
                type="number"
                inputMode="numeric"
                value={scoreTeam2}
                onChange={e => setScoreTeam2(e.target.value)}
                className="w-16 bg-[#0d0409] border border-white/20 rounded px-2 py-1 text-center"
                placeholder="0"
              />
              <button onClick={handleSave} className="flex-1 py-1 bg-green-700 rounded text-sm">✓</button>
              <button onClick={() => setShowScoreInput(false)} className="py-1 px-2 bg-white/10 rounded text-sm">✕</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
